import React from 'react'
import classNames from 'classnames'
import { CTALink } from '../../elements/CTALink'
import { ImageOrAutoplayVideo } from '../../elements/ImageOrAutoplayVideo'
import { RichText } from '../../elements/RichText'
import { Text } from '../../elements/Text'
import answerBotImage from '../../images/product_answer_bot.svg'
import articlesImage from '../../images/product_articles.svg'
import customerDataPlatformImage from '../../images/product_customer_data_platform.svg'
import productInboxImage from '../../images/product_inbox.svg'
import messagesImage from '../../images/product_messages.svg'
import messengerImage from '../../images/product_messenger.svg'
import {
  companyLogoMaxHeight,
  companyLogoMaxWidth,
  containerMaxWidth,
  ColorTheme,
  mq,
  SMARTQUOTE_CLOSE,
  SMARTQUOTE_OPEN,
  AllSystemColors,
  AllColorValues,
  getColorTheme,
  getColor,
  fontTTNormsPro,
} from '../../utils'
import { useSpacing } from 'marketing-site/src/library/providers/spacingProvider'
import { IProps, ProductTypes } from './index'

export function getImageForProduct(productName: ProductTypes): string {
  switch (productName) {
    case 'Answer Bot':
      return answerBotImage
    case 'Articles':
      return articlesImage
    case 'Customer Data Platform':
      return customerDataPlatformImage
    case 'Inbox':
      return productInboxImage
    case 'Messages':
      return messagesImage
    default:
      return messengerImage
  }
}

export interface ILocalTheme extends ColorTheme {
  accentColor: string
}

function getLocalTheme(bgColor: AllSystemColors): ILocalTheme {
  const baseColors = getColorTheme('default', bgColor)
  switch (baseColors.id) {
    case AllColorValues.Stone:
    case AllColorValues.White:
      return {
        ...baseColors,
        accentColor: getColor('default', AllColorValues.Ice),
      }
    default:
      return {
        ...baseColors,
        accentColor: getColor('default', AllColorValues.White),
      }
  }
}

export const Testimonial = ({
  eyebrow,
  quote,
  name,
  jobTitle,
  companyName,
  companyLogo,
  portraitImgRef,
  cta,
  products,
  productText,
  bgColor,
  alignLeft,
  roundedBorders,
  version,
}: IProps) => {
  const nameParsed = `${name}, `
  const themeColors = getLocalTheme(bgColor)
  const hasCTABlock = !!(cta || (products && products.length))
  const alignImageToLeft = !!alignLeft
  const spacing = useSpacing()
  const wrapperClassNames = classNames('testimonial', {
    'testimonial--modal': version,
    'testimonial--padding': !spacing,
  })

  return (
    <div className={wrapperClassNames}>
      <div className="testimonial-content">
        <div className="quote-and-details-block">
          {eyebrow && (
            <span className="eyebrow">
              <Text brand="default" size="eyebrows">
                {eyebrow}
              </Text>
            </span>
          )}
          {version === 'testimonial--modal' && (
            <>
              <span className="modal-name">
                <Text brand="default" size="body-bold">
                  {name}
                </Text>
              </span>
              <span className="modal-jobTitle">
                <Text brand="default" size="body">
                  {jobTitle}
                  {companyName}
                </Text>
              </span>
            </>
          )}
          <blockquote className="quote">
            <Text brand="default" size="caption">
              <RichText html={quote} />
            </Text>
          </blockquote>
          <div className="details-wrapper">
            <p className="name-title">
              <Text brand="default" size="body-bold">
                {nameParsed}
              </Text>
              <Text brand="default" size="body">
                {jobTitle}
              </Text>
            </p>
            {companyLogo ? (
              <div className="company-logo-wrap">
                <img src={companyLogo} alt={companyName} className="company-logo" />
              </div>
            ) : null}
          </div>
        </div>
        {hasCTABlock && (
          <div className="cta-and-products-block">
            {cta && (
              <div className="cta">
                <CTALink arrow {...cta} bgColor={themeColors.CTATheme} />
              </div>
            )}

            {products && products.length && (
              <ul className="product-list">
                {products.map((product) => {
                  return (
                    <li key={product}>
                      <img
                        className="product-list-image"
                        src={getImageForProduct(product)}
                        alt={product}
                      />
                    </li>
                  )
                })}
              </ul>
            )}
            {productText && (
              <div className="product-text">
                <Text brand="default" size="caption">
                  <RichText html={productText} />
                </Text>
              </div>
            )}
          </div>
        )}
        <div className="image-block">
          <ImageOrAutoplayVideo
            imageRef={{
              ...portraitImgRef,
              classNames: 'profile-image',
              borderRadius: 25,
              enableWebm: true,
            }}
          />
        </div>
      </div>
      <style jsx>
        {`
          .testimonial {
            color: ${themeColors.textColor};
            background-color: ${themeColors.backgroundColor};
            padding-left: 0;
            padding-right: 0;
            position: relative;
            width: ${roundedBorders ? '85vw' : '100vw'};
            margin: 0 auto;
            border-radius: ${roundedBorders ? '25px' : 'unset'};
            max-width: ${roundedBorders ? containerMaxWidth : '100%'};

            &--padding {
              padding-top: 3.125rem;
              padding-bottom: 3.125rem;
            }
          }
          .testimonial:after {
            content: '';
            position: absolute;
            background-color: ${roundedBorders ? 'unset' : themeColors.backgroundColor};
            height: 18px;
            left: 50%;
            mask-image: ${roundedBorders ? 'none' : 'url(${edgeTexture})'};
            mask-repeat: repeat no-repeat;
            width: 100vw;
            z-index: -1;
          }
          .testimonial:after {
            bottom: -17px;
            transform: translateX(-50%);
          }
          .testimonial-content {
            display: grid;
            grid-template-columns: repeat(14, 1fr);
            grid-column-gap: 8px;
            max-width: ${containerMaxWidth};
            margin: 0 auto;
          }
          .profile-image {
            width: 100%;
          }
          .quote-and-details-block {
            grid-column: 2 / span 12;
            text-align: center;
          }
          .cta-and-products-block {
            margin-top: 60px;
            grid-column: 2 / span 12;
            display: grid;
            justify-items: center;
          }
          .cta {
            padding-bottom: 60px;
            width: 100%;
            display: grid;
            justify-content: center;
          }
          .image-block {
            grid-row: 1;
            grid-column: 2 / span 12;
            align-self: center;
            padding-bottom: 36px;
            border-radius: 25px;
          }
          .eyebrow {
            display: block;
            margin-bottom: 16px;
          }

          :global(.testimonial .quote .rich-text p:first-child) {
            text-indent: -0.75rem;
            line-height: 1.4;
            font-family: ${fontTTNormsPro};
            font-weight: normal;
            font-size: 1.313rem;
          }

          :global(.testimonial .quote .rich-text p:first-child::before) {
            content: '${SMARTQUOTE_OPEN}';
            position: relative;
            left: -2px;
          }

          :global(.testimonial .quote .rich-text p:last-child::after) {
            content: '${SMARTQUOTE_CLOSE}';
            position: relative;
            left: 2px;
          }

          :global(.product-text .rich-text a) {
            border-bottom: 1px solid;
          }

          .quote {
            margin: 0;
            padding-bottom: 35px;
          }
          .name-title {
            margin-bottom: 6px;
          }
          .company-logo-wrap {
            height: ${companyLogoMaxHeight};
          }
          .company-logo {
            /* only limit height to allow for wide logos with wordmarks */
            max-height: ${companyLogoMaxHeight};
            max-width: ${companyLogoMaxWidth};
          }

          .product-list {
            padding-bottom: 16px;
            text-align: center;
            margin: -3px -6px;
          }
          .product-list li {
            display: inline-block;
            position: relative;
            background: ${themeColors.accentColor};
            height: 42px;
            width: 42px;
            border-radius: 100%;
            margin: 3px 6px;
          }
          .product-list-image {
            display: block;
            position: relative;
            top: 50%;
            transform: translateY(-50%);
            max-width: 42px;
            margin: 0 auto;
          }
          @media (${mq.tablet}) {
            .testimonial {
              grid-column-gap: 16px;
            }
            .quote-and-details-block {
              grid-column: ${alignImageToLeft ? '8 / span 6' : '2 / span 6'};
              align-self: ${hasCTABlock ? 'end' : 'center'};
              text-align: left;
            }
            .cta-and-products-block {
              display: block;
              grid-column: ${alignImageToLeft ? '8 / span 6' : '2 / span 6'};
              justify-items: left;
            }
            .image-block {
              grid-column: ${alignImageToLeft ? '2 / span 6' : '8 / span 6'};
              grid-row: ${hasCTABlock ? '1 / span 2' : '1'};
              align-self: center;
              padding-bottom: 0;
            }
            .cta {
              padding-bottom: ${products ? '80px' : '0'};
              justify-content: left;
            }
            .product-list {
              text-align: left;
            }
            .details-wrapper {
              padding-bottom: ${hasCTABlock ? '36px' : '0'};
            }
          }
          @media (${mq.laptop}) {
            .image-block {
              grid-column: ${alignImageToLeft ? '2 / span 5' : '9 / span 5'};
            }
            :global(.testimonial .quote .rich-text p:first-child) {
              font-size: 1.625rem;
            }
          }
          @media (${mq.desktop}) {
            .testimonial {
              grid-column-gap: 24px;
            }
            .quote-and-details-block {
              grid-column: ${alignImageToLeft ? '9 / span 5' : '2 / span 5'};
            }
            .cta-and-products-block {
              grid-column: ${alignImageToLeft ? '9 / span 5' : '2 / span 5'};
            }
          }
        `}
      </style>
    </div>
  )
}
